import React, { createContext, useContext, useState } from "react";

export const ValidationContext = createContext();

export const ValidationProvider = ({ children }) => {
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePassword = (password) => {
    const passwordRegex = /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d]{8,}$/;
    return passwordRegex.test(password);
  };

  // Validate image dimensions and resize if needed
  const validateImageDimensions = (file, targetWidth, targetHeight) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        if (img.width === targetWidth && img.height === targetHeight) {
          resolve(file); // Dimensions are valid
        } else {
          // Resize image if dimensions don't match
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");
          canvas.width = targetWidth;
          canvas.height = targetHeight;
          ctx.drawImage(img, 0, 0, targetWidth, targetHeight);

          // Convert the resized image back to a file
          canvas.toBlob(
            (blob) => {
              const resizedFile = new File([blob], file.name, {
                type: file.type,
              });
              resolve(resizedFile); // Return resized file
            },
            file.type,
            1
          );
        }
      };
      img.onerror = () => reject("Invalid image file");
      img.src = URL.createObjectURL(file);
    });
  };

  // CNIC validation with auto-formatting
  const validateCnic = (cnic) => {
    // Ensure CNIC is a string to avoid runtime errors
    cnic = typeof cnic === "string" ? cnic : String(cnic || "");
  
    // Remove any non-numeric characters for validation
    const rawCnic = cnic.replace(/\D/g, "");
  
    // CNIC must be exactly 13 digits long
    if (rawCnic.length !== 13) {
      return false;
    }
  
    // Check if the CNIC has exactly 13 digits
    const isValid = /^\d{13}$/.test(rawCnic);
    return isValid;
  };
  
  // Function to format CNIC
  const formatCnic = (cnic) => {
    // Ensure CNIC is a string
    cnic = typeof cnic === "string" ? cnic : String(cnic || "");
  
    // Remove non-digit characters
    cnic = cnic.replace(/\D/g, "");
  
    // Limit to 13 digits
    cnic = cnic.slice(0, 13);
  
    // Format as XXXXX-XXXXXXX-X
    if (cnic.length >= 13) {
      return `${cnic.slice(0, 5)}-${cnic.slice(5, 12)}-${cnic.slice(12)}`;
    } else if (cnic.length >= 6) {
      return `${cnic.slice(0, 5)}-${cnic.slice(5)}`;
    }
    return cnic;
  };
    

  return (
    <ValidationContext.Provider
      value={{
        validateEmail,
        validatePassword,
        validateImageDimensions,
        validateCnic,
        formatCnic,
      }}
    >
      {children}
    </ValidationContext.Provider>
  );
};

export const useValidation = () => useContext(ValidationContext);
